import * as ucans from '@ucans/ucans';
import argon2 from 'argon2-browser';
import { HKDF_KEY_INFO, STORAGE_DID } from './constants';
import tweetnacl from 'tweetnacl';
import hkdf from 'futoin-hkdf';
import { toUint8Array, fromUint8Array } from 'js-base64';

export const generateRandomRSAKeyPair = async () =>
  window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );

export const generateRandomUcanEdKeyPair = async (exportable = true) =>
  await ucans.EdKeypair.create({ exportable });

export const exportKeyPair = async (keyPair: CryptoKeyPair) => {
  const publicKey = await window.crypto.subtle.exportKey(
    'spki',
    keyPair.publicKey
  );
  const privateKey = await window.crypto.subtle.exportKey(
    'pkcs8',
    keyPair.privateKey
  );
  return { publicKey, privateKey };
};

export enum KeyTypes {
  RSA = 'RSA',
  AES = 'AES',
}

const keyAlgoritms = {
  [KeyTypes.AES]: 'AES-GCM',
  [KeyTypes.RSA]: {
    name: 'RSA-OAEP',
    hash: 'SHA-256',
  },
};

export const convertStringToTypedArray = (data: string): Uint8Array => {
  const binaryString = window.atob(data);
  const uint8 = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8[i] = binaryString.charCodeAt(i);
  }
  return uint8;
};

export const importKey = (
  pemContent: string,
  format: 'pkcs8' | 'raw' | 'spki',
  keyType: KeyTypes,
  keyUsage: KeyUsage[]
): Promise<CryptoKey> => {
  const uint8Content = convertStringToTypedArray(pemContent);
  return window.crypto.subtle.importKey(
    format,
    uint8Content.buffer,
    keyAlgoritms[keyType],
    false,
    keyUsage
  );
};

export const importRSAEncryptionKey = (
  pemContent: string
): Promise<CryptoKey> => {
  return importKey(pemContent, 'spki', KeyTypes.RSA, ['encrypt']);
};

export const toRSAPrivateKey = async (pemContent: string) => {
  return await importKey(pemContent, 'pkcs8', KeyTypes.RSA, ['decrypt']);
};

export const decryptUsingRSAKey = async (
  cipherText: string,
  privateKey: string
) => {
  const rsaPrivateKey = await toRSAPrivateKey(privateKey);
  return await window.crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    rsaPrivateKey,
    toUint8Array(cipherText)
  );
};

export const encryptUsingRSAKey = async (
  value: Record<any, any> | string,
  serverEncryptionKey: string
): Promise<string> => {
  try {
    const encryptionKey = await importRSAEncryptionKey(serverEncryptionKey);
    const valueString =
      typeof value === 'string' ? value : JSON.stringify(value);
    const encoded = new TextEncoder().encode(valueString);
    const encrypted = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      encryptionKey,
      encoded
    );

    return fromUint8Array(new Uint8Array(encrypted));
  } catch (error: any) {
    console.log(error);
    throw new Error(error?.message);
  }
};

export const generateAuthToken = async (
  contractAddress: string,
  secretKey: string
) => {
  const keypair = ucans.EdKeypair.fromSecretKey(secretKey.trim());
  const ucan = await ucans.build({
    audience: STORAGE_DID,
    issuer: keypair,
    lifetimeInSeconds: 7 * 86400,
    capabilities: [
      {
        with: {
          scheme: 'storage',
          hierPart: contractAddress.toLowerCase(),
        },
        can: { namespace: 'file', segments: ['CREATE'] },
      },
    ],
  });
  const token = ucans.encode(ucan);
  return token;
};

export const createFileLock = async (
  encryptionKey: string,
  dataKeyMaterial: any
) => {
  return {
    lockedFileKey: await encryptUsingRSAKey(
      dataKeyMaterial.fileKey,
      encryptionKey
    ),
    lockedChatKey: await encryptUsingRSAKey(
      dataKeyMaterial.fileChatKeyPair,
      encryptionKey
    ),
  };
};

export const deriveKeyFromAg2Hash = async (pass: string, salt: Uint8Array) => {
  const key = await argon2.hash({
    pass,
    salt,
    type: argon2.ArgonType.Argon2id,
    hashLen: 32,
    time: 2,
    mem: 102400,
    parallelism: 8,
  });

  return hkdf(Buffer.from(key.hash), tweetnacl.secretbox.keyLength, {
    info: Buffer.from(HKDF_KEY_INFO),
  });
};

export const decryptSecretKey = async (
  docId: string,
  nonce: string,
  encryptedSecretKey: string
) => {
  const derivedKey = await deriveKeyFromAg2Hash(docId, toUint8Array(nonce));

  return tweetnacl.secretbox.open(
    toUint8Array(encryptedSecretKey),
    toUint8Array(nonce),
    derivedKey
  );
};

export const generateRandomAESKey = async (): Promise<string> => {
  const key = await window.crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 128,
    },
    true,
    ['encrypt', 'decrypt']
  );
  return (await window.crypto.subtle.exportKey('jwk', key)).k as string;
};

export const importAESKey = async (keyString: string): Promise<CryptoKey> => {
  const keyData = { kty: 'oct', k: keyString, alg: 'A128GCM', ext: true };
  return await window.crypto.subtle.importKey(
    'jwk',
    keyData,
    {
      name: 'AES-GCM',
    },
    false,
    ['encrypt', 'decrypt']
  );
};

export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const NONCE_LENGTH = 24;
export const KEY_SEPARATOR = '__n__';

export const generateRandomNonce = (length = NONCE_LENGTH) => {
  return window.crypto.getRandomValues(new Uint8Array(length));
};

export const getNonceAppendedCipherText = (
  nonce: Uint8Array,
  cipherText: Uint8Array
) => {
  return (
    fromUint8Array(nonce, true) +
    KEY_SEPARATOR +
    fromUint8Array(cipherText, true)
  );
};

export const getNonceAndCipherText = (
  nonceAppendedCipherText: string,
  defaultNonce: string
) => {
  if (nonceAppendedCipherText.includes(KEY_SEPARATOR)) {
    const [nonce, cipherText] = nonceAppendedCipherText.split(KEY_SEPARATOR);
    return { nonce, cipherText };
  }
  return { nonce: defaultNonce, cipherText: nonceAppendedCipherText };
};

export const encryptData = async (
  data: string,
  keyString: string
): Promise<{ iv: Uint8Array; encryptedData: string }> => {
  const key = await importAESKey(keyString);
  const iv = generateRandomNonce();
  const encodedData = new TextEncoder().encode(data);
  const encryptedData = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    key,
    encodedData
  );
  return {
    iv,
    encryptedData:
      arrayBufferToBase64(iv) +
      KEY_SEPARATOR +
      arrayBufferToBase64(encryptedData),
  };
};

export const decryptData = async (
  _encryptedData: string,
  keyString: string,
  nonce: ArrayBuffer
): Promise<string> => {
  const encryptedData = base64ToArrayBuffer(_encryptedData);
  const key = await importAESKey(keyString);

  const decryptedData = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
    },
    key,
    encryptedData
  );
  return new TextDecoder().decode(decryptedData);
};
